import { useState } from 'react';
import { mensagemDoErro } from '../../api/erros.js';
import { definirCredencialDoErp } from '../../api/integracao-erp.js';
import { CampoTexto } from '../../componentes/Campo.js';
import { FormularioLateral } from '../../componentes/FormularioLateral.js';

type Props = {
  /** Havia credencial antes? Muda o verbo do formulário, e nada mais. */
  readonly substituindo: boolean;
  readonly aoFechar: () => void;
  readonly aoConcluir: (mensagem: string) => void;
};

/**
 * Usuário e senha do ERP — o único lugar do painel por onde a senha passa.
 *
 * Ela vai uma vez, para o cofre gerenciado, e não volta: a tela nunca a
 * apresenta preenchida, nem em campo de senha mascarado, porque o middleware
 * não a devolve em resposta nenhuma (RNF-073). Por isso não existe "editar
 * credencial" — existe **substituir**, e quem substitui digita a senha inteira.
 *
 * `autoComplete="new-password"` não é detalhe de estilo: sem ele o navegador
 * oferece a senha do PAINEL para o campo da senha do ERP, e a pessoa a envia
 * sem perceber.
 */
export function FormularioDaCredencial({ substituindo, aoFechar, aoConcluir }: Props) {
  const [usuario, definirUsuario] = useState('');
  const [senha, definirSenha] = useState('');
  const [erros, definirErros] = useState<Readonly<Record<string, string>>>({});
  const [erro, definirErro] = useState<string | null>(null);
  const [enviando, definirEnviando] = useState(false);

  function confirmar() {
    const encontrados: Record<string, string> = {};

    if (usuario.trim() === '') {
      encontrados['usuario'] = 'Informe o usuário de integração do ERP.';
    }
    if (senha === '') {
      encontrados['senha'] = 'Informe a senha.';
    }

    definirErros(encontrados);

    if (Object.keys(encontrados).length > 0) {
      return;
    }

    definirErro(null);
    definirEnviando(true);

    definirCredencialDoErp({ usuario: usuario.trim(), senha })
      .then((estado) => {
        definirEnviando(false);
        aoConcluir(
          `Credencial gravada no cofre para o usuário ${estado.usuario ?? usuario.trim()}.`,
        );
      })
      .catch((falha: unknown) => {
        definirEnviando(false);
        definirErro(mensagemDoErro(falha));
      });
  }

  return (
    <FormularioLateral
      titulo={substituindo ? 'Substituir credencial do ERP' : 'Credencial do ERP'}
      subtitulo="Vai para o cofre gerenciado. Não fica no banco da plataforma nem no coletor."
      rotuloConfirmar={substituindo ? 'Substituir' : 'Gravar credencial'}
      aoConfirmar={confirmar}
      aoFechar={aoFechar}
      enviando={enviando}
      {...(erro === null ? {} : { erro })}
    >
      <CampoTexto
        rotulo="Usuário de integração"
        valor={usuario}
        aoMudar={definirUsuario}
        obrigatorio
        fonte="mono"
        autoCompletar="off"
        ajuda="O usuário do Senior que o middleware usa. Sem espaços."
        {...(erros['usuario'] === undefined ? {} : { erro: erros['usuario'] })}
      />

      <CampoTexto
        rotulo="Senha"
        valor={senha}
        aoMudar={definirSenha}
        tipo="password"
        obrigatorio
        autoCompletar="new-password"
        ajuda="Enviada uma única vez. O painel não a exibe depois — para trocar, digite a nova por inteiro."
        {...(erros['senha'] === undefined ? {} : { erro: erros['senha'] })}
      />
    </FormularioLateral>
  );
}
